/**
 * Order List Header Component Controller (header.component.ts).
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import { Component, Inject, Input, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { OrderFiltersComponent } from '../../../../../components/modals/order-filters/order-filters.component';
import { WdOrdersService } from '../../../../../services/orders/wd-orders.service';
import { WdLocalEnvironmentService } from '../../../../../services/environment/wd-local-environment.service';
import { ManageLookaheadsComponent } from '../../../../../components/modals/order-lookaheads/manage-lookaheads.component';
import { WdJobService } from '../../../../../services/jobs/wd-jobs.service';
import { WdLocalSharedFunctionsService } from '../../../../../services/shared/wd-local-shared-functions.service';
import { WdOrdersV2Service } from '../../../../../services/orders/wd-orders-v2.service';
import { IBcxErrorService } from '../../../../../interfaces/generic/generic-interfaces';

@Component({
  selector: 'app-orders-list-header-component',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ListHeaderComponent {
  /**
   * Constructor Initialization
   * @since 4.34.0
   */
  constructor(
    private matDialog: MatDialog,
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    public wdEnvironmentService: WdLocalEnvironmentService,
    public wdJobService: WdJobService,
    public wdOrdersService: WdOrdersService,
    public wdOrdersV2Service: WdOrdersV2Service,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
  ) {}

  // Order String Inputs
  @Input() orderType: string = this.wdLocalSharedFunctions.orderTypeKey.WORK;
  @Input() pageTitle?: string;
  @Input() pageSubtitle?: string;
  // Order Boolean Inputs
  @Input() showHeader?: boolean = true;
  @Input() showFilters?: boolean = true;

  // Pagination Variables
  pageSizeOptions: Array<any> = [15, 25, 50, 75, 100];

  // Update Status
  updateStatusesValue: any = null;
  updatingStatuses = false;

  /**
   * Update statuses for selected orders
   * @since 4.34.0
   */
  async updateOrderStatuses(status: any): Promise<void> {
    this.updatingStatuses = true;
    try {
      const indexes: any = [];
      this.wdOrdersService.orderListSelections.selected.forEach(
        (order: any) => {
          // if (order.status.type !== status.type) {
          indexes.push(order.index);
          // }
        },
      );
      // Update Status
      await this.wdOrdersService.wdOrdersV2Service.updateOrderStatuses(
        indexes,
        status?.type,
      );
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersV2Service.fetchOrders(false);
      this.updatingStatuses = false;
      setTimeout(() => {
        this.updateStatusesValue = null;
        this.wdOrdersService.orderListSelections.clear();
      }, 0);
    }
  }

  /**
   * Open Add Lookahead Window
   * @since 4.36.0
   */
  addLookahead() {
    // Open Lookahead Dialog
    this.matDialog.open(ManageLookaheadsComponent, {
      disableClose: true,
      data: {
        onSuccessCallback: () => {
          // Reload Orders
          this.reloadOrders();
        },
      },
    });
  }

  /**
   * Triggered when filters are changed
   * @since 4.34.0
   */
  filtersChanged(): void {
    // Set dynamic fulfillment group column sizing
    this.wdOrdersService.setFulfillmentGroupColumnSizing();
    // Save Selected API Filters
    this.wdOrdersService.saveSelectedFilters();
    // Reload Orders
    this.reloadOrders();
  }

  /**
   * Go to first page of orders
   * @since 4.34.0
   */
  goFirstPage() {
    this.resetNavigation();
    this.filtersChanged();
  }

  /**
   * Go to next page of orders
   * @since 4.34.0
   */
  goNextPage() {
    this.wdOrdersV2Service.orderViewSettings.page++;
    const orders: Array<any> = this.wdOrdersV2Service.allOrders.value;
    // Set Last ID Filter
    if (orders.length > 0) {
      this.wdOrdersV2Service.orderFilters.value.lastId =
        orders[orders.length - 1].id;
    }
    this.filtersChanged();
  }

  /**
   * Handle Filters Button Click (Opens Filters Modal)
   * @since 4.34.0
   */
  handleFiltersClick() {
    // Open Dialog
    this.matDialog.open(OrderFiltersComponent, {
      width: '600px',
      data: {
        filters: this.wdOrdersV2Service.orderFilters,
        changesCallback: () => {
          return this.filtersChanged();
        },
      },
    });
  }

  /**
   * Reset Navigation
   * @since 4.34.0
   */
  resetNavigation() {
    this.wdOrdersV2Service.orderViewSettings.page = 1;
    this.wdOrdersV2Service.orderFilters.value.lastId = '';
  }

  /**
   * Reload Orders
   * @since 4.34.0
   */
  reloadOrders() {
    // Reload Orders with API Filtering
    void this.wdOrdersV2Service.fetchOrders(false);
  }

  /**
   * Set Page Size Options
   *
   * @since 4.34.0
   */
  setPageSize($size: any, triggerFiltering = false): void {
    // Reset Navigation
    this.resetNavigation();
    // Set Page Size Filter
    this.wdOrdersV2Service.orderViewSettings.pageSize = $size;
    // Reload Orders
    if (triggerFiltering) {
      this.filtersChanged();
      // Save Settings to Local Storage
      this.wdOrdersService.saveLocalStorageSettings();
    }
  }

  /**
   * Set Toggled Columns
   * @since 4.38.0
   */
  setToggledColumns(): void {
    this.wdOrdersService.setOrderListColumns(this.orderType);
  }

  /**
   * Set Toggle Empty Fulfillment Groups Option
   * @since 4.34.0
   */
  setToggleEmptyFulfillmentGroupsOption($value: boolean): void {
    this.wdOrdersV2Service.orderViewSettings.groups.fulfillment.showEmpty = $value;
    // Set dynamic fulfillment group column sizing
    this.wdOrdersService.setFulfillmentGroupColumnSizing();
    // Save Settings to Local Storage
    this.wdOrdersService.saveLocalStorageSettings();
  }

  /**
   * On Blur Job Typeahead
   * @since 4.37.0
   */
  typeaheadJobBlur(model: any) {
    // Clear value if no model selected
    if (!model?.index) {
      this.wdOrdersV2Service.orderFilters.value.jobFilter = null;
    }
  }
}
